
import Vue from "vue";
import InfiniteLoading, { StateChanger } from "vue-infinite-loading";
import SiteFooter from "@/components/organisms/Footer.vue";
import Loading from "@/components/atoms/Loading.vue";
import NotificationList from "@/components/organisms/NotificationList.vue";
import {
  Notification,
  NotificationFromNolaNovel,
  NotificationsFromNolaNovel,
  NotificationDataType,
  ConnectedOtherServices,
} from "@/lib/models";

type TabType = "agent" | "nolaNovel";
type SubTabType = "all" | "reader" | "editor" | "another" | "operation" | "agentNotif";

const AGENT_SUB_TABS = [
  { value: "all" as SubTabType, label: "すべて" },
  { value: "operation" as SubTabType, label: "運営" },
  { value: "agentNotif" as SubTabType, label: "エージェント" },
];

const NOLA_NOVEL_SUB_TABS = [
  { value: "all" as SubTabType, label: "すべて" },
  { value: "reader" as SubTabType, label: "読者より" },
  { value: "editor" as SubTabType, label: "編集者より" },
  { value: "another" as SubTabType, label: "その他" },
];

// 1回のAPI呼び出しで取得する件数
const FETCH_LIMIT = 50;

export default Vue.extend<Data, Methods, Computed, Props>({
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    SiteFooter,
    Loading,
    NotificationList,
    InfiniteLoading,
  },
  data() {
    return {
      currentTab: "agent" as TabType,
      currentSubTab: "all" as SubTabType,
      isInitialLoaded: false,
      isLoading: false,
      // エージェント通知
      agentNotifications: [] as Notification[],
      agentLastKey: null as string | null,
      agentComplete: false,
      // Nolaノベル通知（storeから取得）
      nolaNovelUnreadComplete: false,
      nolaNovelFirstReadComplete: false,
      nolaNovelReadComplete: false,
      readAllTimeout: null as ReturnType<typeof setTimeout> | null,
      readAgentTimeout: null as ReturnType<typeof setTimeout> | null,
    };
  },
  computed: {
    currentSubTabs() {
      return this.currentTab === "agent" ? AGENT_SUB_TABS : NOLA_NOVEL_SUB_TABS;
    },
    unreadNolaNovelNotifications() {
      return this.$store.getters["nolaNovelModule/notifications"](false) as NotificationsFromNolaNovel;
    },
    readNolaNovelNotifications() {
      return this.$store.getters["nolaNovelModule/notifications"](true) as NotificationsFromNolaNovel;
    },
    filteredNotifications() {
      if (this.currentTab === "agent") {
        return this.filterAgentNotifications(this.agentNotifications, this.currentSubTab);
      }
      // Nolaノベル通知は未読→既読の順で結合してフィルタ
      // 未読を先に表示し、未読取得完了後に既読を表示
      const unreadItems = this.filterNolaNovelNotifications(
        this.unreadNolaNovelNotifications.items,
        this.currentSubTab
      );
      const readItems = this.nolaNovelUnreadComplete
        ? this.filterNolaNovelNotifications(this.readNolaNovelNotifications.items, this.currentSubTab)
        : [];
      // 結合してから重複を排除（未読を優先）
      const combined = [...unreadItems, ...readItems];
      const seen = new Set<string>();
      return combined.filter((item: NotificationFromNolaNovel) => {
        if (seen.has(item.typeTargetIdSubTargetId)) {
          return false;
        }
        seen.add(item.typeTargetIdSubTargetId);
        return true;
      });
    },
    showInfiniteLoading() {
      if (this.currentTab === "agent") {
        // 運営タブは通知がないのでローダーを表示しない
        if (this.currentSubTab === "operation") {
          return false;
        }
        return !this.agentComplete;
      }
      return !this.nolaNovelUnreadComplete || !this.nolaNovelReadComplete;
    },
    infiniteLoadingKey() {
      // タブとサブタブ、完了状態を組み合わせたキーでコンポーネントを再マウント
      return `infinite-${this.currentTab}-${this.currentSubTab}`;
    },
    isLoadComplete() {
      if (this.currentTab === "agent") {
        // 運営タブは常に完了（データがない）
        if (this.currentSubTab === "operation") {
          return true;
        }
        return this.agentComplete;
      }
      // Nolaノベルは未読と既読の両方が完了した場合
      return this.nolaNovelUnreadComplete && this.nolaNovelReadComplete;
    },
    isNolaNovelConnected() {
      const connectedOtherServices = this.$store.getters[
        "userModule/connectedOtherServices"
      ] as ConnectedOtherServices;
      return connectedOtherServices?.nolaNovel?.result === true;
    },
  },
  async created() {
    // URLからタブ状態を復元
    this.initTabFromRoute();

    // 初回データ取得
    await this.fetchInitialData();
    this.isInitialLoaded = true;

    // 3秒後にエージェント通知を既読にする
    this.readAgentTimeout = setTimeout(() => {
      this.readAgentNotifications();
    }, 3000);
  },
  watch: {
    "$route.params.tab": {
      handler(newTab: string) {
        const tab = newTab === "nolanovel" ? "nolaNovel" : "agent";
        if (this.currentTab !== tab) {
          this.handleTabChange(tab);
        }
      },
    },
  },
  beforeDestroy() {
    if (this.readAllTimeout) {
      clearTimeout(this.readAllTimeout);
    }
    if (this.readAgentTimeout) {
      clearTimeout(this.readAgentTimeout);
    }
  },
  methods: {
    initTabFromRoute() {
      const tab = this.$route.params.tab;
      if (tab === "nolanovel") {
        this.currentTab = "nolaNovel";
      } else {
        this.currentTab = "agent";
      }
    },
    async fetchInitialData() {
      // 初回はエージェント通知とNolaノベル未読通知を取得
      // 既読通知は無限スクロールで取得
      if (this.isNolaNovelConnected) {
        await Promise.all([this.fetchAgentNotifications(), this.fetchNolaNovelUnreadNotifications()]);
      } else {
        // Nolaノベルのアカウントが連携されていない場合は完了フラグを立てる
        this.nolaNovelUnreadComplete = true;
        this.nolaNovelReadComplete = true;
        await this.fetchAgentNotifications();
      }
    },
    onTabChange(tab: TabType) {
      // 現在のタブと同じ場合は何もしない
      if (this.currentTab === tab) {
        return;
      }

      // パスを変更（analyticsのページビューイベントが発火する）
      const path = tab === "agent" ? "/inbox/nola" : "/inbox/nolanovel";
      this.$router.push(path);
    },
    handleTabChange(tab: TabType) {
      this.currentTab = tab;
      this.currentSubTab = "all";

      if (tab === "nolaNovel") {
        // 未読取得が完了している場合は3秒後に全通知を既読にする
        if (this.nolaNovelUnreadComplete) {
          this.scheduleReadAllNolaNovelNotifications();
        }
      }

      // タブ切替時にInfiniteLoadingをリセット
      this.$nextTick(() => {
        const infiniteLoading = this.$refs.infiniteLoading as InfiniteLoading | undefined;
        if (infiniteLoading && infiniteLoading.stateChanger) {
          infiniteLoading.stateChanger.reset();
        }
      });
    },
    onSubTabChange(subTab: SubTabType) {
      this.currentSubTab = subTab;

      // サブタブ切替時にInfiniteLoadingをリセット（まだデータがあれば）
      if (this.showInfiniteLoading) {
        this.$nextTick(() => {
          const infiniteLoading = this.$refs.infiniteLoading as InfiniteLoading | undefined;
          if (infiniteLoading && infiniteLoading.stateChanger) {
            infiniteLoading.stateChanger.reset();
          }
        });
      }
    },
    async handleInfinite($state: StateChanger) {
      if (this.isLoading) {
        // ローディング中の場合は一旦リセットして後で再トリガー
        const infiniteLoading = this.$refs.infiniteLoading as InfiniteLoading | undefined;
        if (infiniteLoading && infiniteLoading.stateChanger) {
          infiniteLoading.stateChanger.reset();
        }
        return;
      }
      this.isLoading = true;

      try {
        if (this.currentTab === "agent") {
          await this.handleAgentInfinite($state);
        } else {
          await this.handleNolaNovelInfinite($state);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async handleAgentInfinite($state: StateChanger) {
      const result = await this.$store.dispatch("notificationModule/listNotifications", {
        notificationGroup: "agent",
        limit: FETCH_LIMIT,
        lastKey: this.agentLastKey,
      });

      // 取得した通知を既読にする
      this.readAgentNotificationsImmediate(result.items);

      this.agentNotifications.push(...result.items);
      this.agentLastKey = result.lastKey;

      if (!result.lastKey) {
        // これ以上データがない場合は完了
        this.agentComplete = true;
        $state.complete();
      } else {
        // まだデータがある場合はロード続行
        $state.loaded();
      }
    },
    async handleNolaNovelInfinite($state: StateChanger) {
      // Nolaノベルのアカウントが連携されていない場合は完了
      if (!this.isNolaNovelConnected) {
        this.nolaNovelUnreadComplete = true;
        this.nolaNovelReadComplete = true;
        $state.complete();
        return;
      }
      // まず未読通知を取得しきる
      if (!this.nolaNovelUnreadComplete) {
        await this.handleNolaNovelUnreadInfinite($state);
        return;
      }
      // 次に既読通知を取得
      await this.handleNolaNovelReadInfinite($state);
    },
    async handleNolaNovelUnreadInfinite($state: StateChanger) {
      if (this.unreadNolaNovelNotifications.lastKey) {
        await this.$store.dispatch("nolaNovelModule/loadNotifications", { isRead: false });
        // 追加取得後にまだデータがあるかチェック
        if (!this.unreadNolaNovelNotifications.lastKey) {
          this.nolaNovelUnreadComplete = true;
          // 3秒後に全通知を既読にする
          this.scheduleReadAllNolaNovelNotifications();
        }
        $state.loaded();
      } else {
        // 未読を取得し切った
        this.nolaNovelUnreadComplete = true;
        // 3秒後に全通知を既読にする
        this.scheduleReadAllNolaNovelNotifications();
        $state.loaded();
      }
    },
    async handleNolaNovelReadInfinite($state: StateChanger) {
      if (!this.nolaNovelFirstReadComplete) {
        // 既読通知の初回取得
        await this.$store.dispatch("nolaNovelModule/fetchNotifications", { isRead: true });
        this.nolaNovelFirstReadComplete = true;
        // 取得後にまだデータがあるかチェック
        if (!this.readNolaNovelNotifications.lastKey) {
          this.nolaNovelReadComplete = true;
          $state.complete();
        } else {
          $state.loaded();
        }
      } else if (this.readNolaNovelNotifications.lastKey) {
        // 既読通知の追加取得
        await this.$store.dispatch("nolaNovelModule/loadNotifications", { isRead: true });
        // 追加取得後にまだデータがあるかチェック
        if (!this.readNolaNovelNotifications.lastKey) {
          this.nolaNovelReadComplete = true;
          $state.complete();
        } else {
          $state.loaded();
        }
      } else {
        // 全て取得完了
        this.nolaNovelReadComplete = true;
        $state.complete();
      }
    },
    async fetchAgentNotifications() {
      const result = await this.$store.dispatch("notificationModule/listNotifications", {
        notificationGroup: "agent",
        limit: FETCH_LIMIT,
        lastKey: null,
      });

      this.agentNotifications = result.items;
      this.agentLastKey = result.lastKey;
      this.agentComplete = !result.lastKey;
    },
    async fetchNolaNovelUnreadNotifications() {
      const unreadNotifications = this.unreadNolaNovelNotifications;
      if (unreadNotifications.items.length === 0) {
        await this.$store.dispatch("nolaNovelModule/fetchNotifications", { isRead: false });
      }
      // 未読がない場合は完了フラグを立てる
      const updated = this.$store.getters["nolaNovelModule/notifications"](false) as NotificationsFromNolaNovel;
      if (!updated.lastKey) {
        this.nolaNovelUnreadComplete = true;
      }
    },
    readAgentNotificationsImmediate(notifications: Notification[]) {
      const unreadIds = notifications.filter((n: Notification) => !n.isRead).map((n: Notification) => n.notificationId);
      if (unreadIds.length > 0) {
        this.$store.dispatch("notificationModule/readNotifications", {
          notificationGroup: "agent",
          notificationIds: unreadIds,
        });
      }
    },
    async readAgentNotifications() {
      const unreadIds = this.agentNotifications
        .filter((n: Notification) => !n.isRead)
        .map((n: Notification) => n.notificationId);
      if (unreadIds.length > 0) {
        await this.$store.dispatch("notificationModule/readNotifications", {
          notificationGroup: "agent",
          notificationIds: unreadIds,
        });
        this.agentNotifications = this.agentNotifications.map((n: Notification) => ({
          ...n,
          isRead: true,
        }));
      }
    },
    scheduleReadAllNolaNovelNotifications() {
      if (this.readAllTimeout) {
        clearTimeout(this.readAllTimeout);
      }
      this.readAllTimeout = setTimeout(() => {
        this.readAllNolaNovelNotifications();
      }, 3000);
    },
    readAllNolaNovelNotifications() {
      if (this.unreadNolaNovelNotifications.items.length > 0) {
        this.$store.dispatch("nolaNovelModule/readAllNotifications");
      }
    },
    filterAgentNotifications(notifications: Notification[], subTab: SubTabType): Notification[] {
      switch (subTab) {
        case "all":
          return notifications;
        case "operation":
          // 運営からの通知は現時点では存在しない
          return [];
        case "agentNotif":
          // エージェントからの通知
          return notifications;
        default:
          return notifications;
      }
    },
    filterNolaNovelNotifications(
      notifications: NotificationFromNolaNovel[],
      subTab: SubTabType
    ): NotificationFromNolaNovel[] {
      switch (subTab) {
        case "all":
          return notifications;
        case "reader":
          return notifications.filter((n) => this.isReaderNotification(n));
        case "editor":
          return notifications.filter((n) => this.isEditorNotification(n));
        case "another":
          return notifications.filter((n) => !this.isReaderNotification(n) && !this.isEditorNotification(n));
        default:
          return notifications;
      }
    },
    isReaderNotification(notification: NotificationFromNolaNovel): boolean {
      return (
        notification.type === NotificationDataType.BOOKMARK ||
        notification.type === NotificationDataType.FOLLOW ||
        notification.type === NotificationDataType.REVIEW ||
        notification.type === NotificationDataType.COMMENT ||
        notification.type === NotificationDataType.COMMENT_REPLY ||
        notification.type === NotificationDataType.COMMENT_ACTIVITY ||
        notification.type === NotificationDataType.COMMENT_ACTIVITY_REPLY ||
        notification.type === NotificationDataType.FAVORITE_ACTIVITY
      );
    },
    isEditorNotification(notification: NotificationFromNolaNovel): boolean {
      return (
        notification.type === NotificationDataType.COMPANY_FOOTPRINTS ||
        notification.type === NotificationDataType.COMPANY_FOOTPRINTS_EPISODE ||
        notification.type === NotificationDataType.COMPANY_FEATURED_NOVEL ||
        notification.type === NotificationDataType.COMPANY_BOOKMARK ||
        notification.type === NotificationDataType.ADMIN_PICKUP
      );
    },
  },
});

interface Props {}

interface Data {
  currentTab: TabType;
  currentSubTab: SubTabType;
  isInitialLoaded: boolean;
  isLoading: boolean;
  agentNotifications: Notification[];
  agentLastKey: string | null;
  agentComplete: boolean;
  nolaNovelUnreadComplete: boolean;
  nolaNovelFirstReadComplete: boolean;
  nolaNovelReadComplete: boolean;
  readAllTimeout: ReturnType<typeof setTimeout> | null;
  readAgentTimeout: ReturnType<typeof setTimeout> | null;
}

interface Computed {
  currentSubTabs: Array<{ value: SubTabType; label: string }>;
  unreadNolaNovelNotifications: NotificationsFromNolaNovel;
  readNolaNovelNotifications: NotificationsFromNolaNovel;
  filteredNotifications: Notification[] | NotificationFromNolaNovel[];
  showInfiniteLoading: boolean;
  infiniteLoadingKey: string;
  isLoadComplete: boolean;
  isNolaNovelConnected: boolean;
}

interface Methods {
  initTabFromRoute(): void;
  fetchInitialData(): Promise<void>;
  onTabChange(tab: TabType): void;
  handleTabChange(tab: TabType): void;
  onSubTabChange(subTab: SubTabType): void;
  handleInfinite($state: StateChanger): Promise<void>;
  handleAgentInfinite($state: StateChanger): Promise<void>;
  handleNolaNovelInfinite($state: StateChanger): Promise<void>;
  handleNolaNovelUnreadInfinite($state: StateChanger): Promise<void>;
  handleNolaNovelReadInfinite($state: StateChanger): Promise<void>;
  fetchAgentNotifications(): Promise<void>;
  fetchNolaNovelUnreadNotifications(): Promise<void>;
  readAgentNotificationsImmediate(notifications: Notification[]): void;
  readAgentNotifications(): Promise<void>;
  scheduleReadAllNolaNovelNotifications(): void;
  readAllNolaNovelNotifications(): void;
  filterAgentNotifications(notifications: Notification[], subTab: SubTabType): Notification[];
  filterNolaNovelNotifications(
    notifications: NotificationFromNolaNovel[],
    subTab: SubTabType
  ): NotificationFromNolaNovel[];
  isReaderNotification(notification: NotificationFromNolaNovel): boolean;
  isEditorNotification(notification: NotificationFromNolaNovel): boolean;
}
